/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2021, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */
import { useMemo, useEffect, useState } from 'react';
import generateAvroExample from '@ovotech/avro-mock-generator';
import { useTranslation } from 'react-i18next';
import generator from '../utils/generator';
import { useApi } from '../contexts/ApiHandler';

const useAsyncAPIExamplePayload = (channelName, operationId) => {
  const { apiHandler } = useApi();
  const [exampleState, updateExampleState] = useState({ loading: true, example: null, error: null });
  const { example, payload, type } = useMemo(() => apiHandler.getFirstSchema(channelName, operationId), [apiHandler, channelName, operationId]);
  const { t } = useTranslation();

  useEffect(() => {
    const generateExample = async (example, payload, type) => {
      let generatedExample = null;
      let error = null;
      const isPrimitive = apiHandler.isPrimitiveSchema(payload);

      if (example) {
        updateExampleState({ loading: false, example, error });
      } else {
        if (payload) {
          try {
            if (apiHandler.isAvroSchema(type) && !isPrimitive) {
              if(payload._json && payload._json['x-parser-original-payload']){
                generatedExample = generateAvroExample(payload._json['x-parser-original-payload']);
              } else {
                const data = await generator.generateAsyncApiResponse({
                  api: apiHandler.api,
                  schema: payload,
                  contentType: 'application/json',
                  highlight: false
                });
                generatedExample = JSON.parse(data.example);
              }
            } else if (apiHandler.isAsyncApiSchema(type) || apiHandler.isOpenApiSchema(type) || apiHandler.isJSONSchema(type) || isPrimitive) {
              const data = await generator.generateAsyncApiResponse({
                api: apiHandler.api,
                schema: payload,
                contentType: 'application/json',
                highlight: false
              });
              generatedExample = JSON.parse(data.example);
            } else {
              generatedExample = t('unsupported_schema_format', { type });
            }
         } catch (err) { 
            const exampleError = err.message ? err.message : err;
            error = t('example_generator_error', { error: exampleError });
          }
        }

        updateExampleState({ loading: false, example: generatedExample, error });
      }
    };

    generateExample(example, payload, type);
  }, [example, payload, type, apiHandler, t]);

  return exampleState;
};

const useAsyncAPIExampleHeader = (channelName) => {
  const { apiHandler } = useApi();
  const [exampleState, updateExampleState] = useState({ loading: true, example: null, error: null });
  const { schema, example } = useMemo(() => apiHandler.getFirstMessageHeader(channelName), [apiHandler, channelName]);
  const { t } = useTranslation();

  useEffect(() => {
    const generateExample = async (example, schema) => {
      let generatedExample = null;
      let error = null;

      if (example) {
        updateExampleState({ loading: false, example, error });
      } else {
        if (schema) {
          try {
            const data = await generator.generateAsyncApiResponse({
              api: apiHandler.api,
              schema,
              contentType: 'application/json',
              highlight: false
            });
            generatedExample = JSON.parse(data.example);
          } catch (err) {
            const exampleError = err.message ? err.message : err;
            error = t('example_generator_error', { error: exampleError });
          }
        }

        updateExampleState({ loading: false, example: generatedExample, error });
      }
    };

    generateExample(example, schema);
  }, [example, schema, apiHandler, t]);

  return exampleState;
};

export { useAsyncAPIExamplePayload, useAsyncAPIExampleHeader };
